import { PageProps } from 'gatsby';
import React, { useEffect } from 'react';
import { gTagEventWithRetry } from 'src/functions/googleAnalyticsHelper';
import useExperimentCustom from 'src/hooks/useExperimentCustom';
import { ExperimentSharedProvider } from 'src/hooks/useExperimentSharedContext';

type AbTestingServiceProps<TControl, TVariant> = {
  experimentName?: string;
  getterLabel?: string;
  props?: PageProps;
  control: { data?: TControl; template: React.FunctionComponent<any> };
  variant?: { data?: TVariant; template: React.FunctionComponent<any> };
};

const AbTestingService = <TControl, TVariant>(
  props: AbTestingServiceProps<TControl, TVariant>
): JSX.Element => {
  const Control = props.control.template as React.FunctionComponent<any>;
  const controlProps = Object.assign({}, props.props, {
    data: props.control.data,
  });
  const isScriptless = process.env.GATSBY_SITE_ENV === 'scriptless';

  if (!props.experimentName || isScriptless) {
    return <Control {...controlProps} />;
  }

  const experimentResult = useExperimentCustom<boolean>([
    {
      experiment: props.experimentName,
      default: false,
      getter: props.getterLabel,
    },
  ]);

  // Page getter values should always be boolean
  const experimentSetup = experimentResult[props.getterLabel];

  const useVariant = experimentSetup.value;

  const Variant = props.variant?.template as React.FunctionComponent<any>;
  const variantProps = Object.assign({}, props.props, {
    data: props.variant?.data,
  });

  useEffect(() => {
    if (experimentSetup?.isLoading) return;

    // There's a possibility that gtag isn't available by the time the experiment result is ready
    // So we need to retry until it's available
    gTagEventWithRetry('Experiment_Result', {
      category: `experiment_${props.experimentName}`,
      label: useVariant ? 'variant' : 'control',
    });
  }, [experimentSetup]);

  if (experimentSetup.isLoading) {
    return (
      <div style={{ opacity: 0 }}>
        <Control {...controlProps} />
      </div>
    );
  }

  const VariantToUse = useVariant ? Variant : Control;
  const propsToUse = useVariant ? variantProps : controlProps;

  return (
    <ExperimentSharedProvider
      experiment={props.experimentName}
      value={useVariant}
    >
      <VariantToUse {...propsToUse} />
    </ExperimentSharedProvider>
  );
};

export default AbTestingService;
